import { ChangeEvent, FC, InputHTMLAttributes, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import styles from 'components/atoms/inputs/RadioButton/RadioButton.styl'
import cn from 'classnames'
import Flex from 'components/atoms/Flex/Flex'
import { MaskOptions } from 'components/atoms/inputs/maskedInput/MaskedInput/MaskedInput'
import Input, { InputBasicType } from 'components/atoms/inputs/Input/Input'

type RadioTextComboProps =
  & Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'value'>
  & {
    radioName: string
    radioValue: string
    name: string
    label?: string
    text?: string | number
    hint?: string
    displayValue?: string
    wrapperClassName?: string
    inputClassName?: string
    dotClassName?: string
    isHighlighted?: boolean
    withBorder?: boolean
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void
    onRadioChange?: (event: ChangeEvent<HTMLInputElement>) => void
    mask?: MaskOptions
    inputType?: InputBasicType
  }

/**
 * Designed to work with react-hook-form via useFormContext()
 * Should be a descendant of <FormProvider>
 */
const RadioTextCombo: FC<RadioTextComboProps> = (props) => {
  const {
    name,
    inputType,
    label,
    radioName,
    radioValue,
    text,
    hint,
    displayValue,
    children,
    className,
    inputClassName,
    wrapperClassName,
    dotClassName,
    withBorder,
    isHighlighted,
    onRadioChange,
    onChange,
    mask,
    ...restProps
  } = props

  const formContext = useFormContext()
  const { register, watch, setValue } = formContext

  const isSelected = watch(radioName) === radioValue

  const handleInputFocus = useCallback(() => {
    setValue(radioName, radioValue)
  }, [radioName, radioValue, setValue])

  const classNames = useMemo(() => cn(
    styles.radioButtonCombo,
    wrapperClassName, {
      [styles.withBorder]: !!withBorder,
      [styles.isSelected]: !!isSelected,
      [styles.isHighlighted]: !!isHighlighted,
    }
  ), [isHighlighted, isSelected, withBorder, wrapperClassName])

  const radioProps = register(radioName)
  const hiddenRadioButton = useMemo(() => !!register && (
    <input
      aria-hidden='true'
      className={ cn(styles.input, className) }
      type='radio'
      value={ radioValue }
      { ...radioProps }
      onChange={ onRadioChange || radioProps.onChange }
      { ...restProps }
      readOnly
    />
  ), [className, register, radioName, restProps, radioValue])


  const hintChild = useMemo(() => !!hint && (
    <small>{ hint }</small>
  ), [hint])

  const textChild = useMemo(() => !!text && (
    <p>{text}</p>
  ), [text])

  const labelTextChild = useMemo(() => !!label && (
    <span className={ styles.labelText }>{ label }</span>
  ), [label])

  const displayValueChild = useMemo(() => !!displayValue && (
    <span className={ styles.displayValue }>
      { displayValue }
    </span>
  ), [displayValue])

  return (
    <label className={ classNames }>
      <Flex gap='spread' horizontal>
        <Flex gap='tiny' horizontal>
          { hiddenRadioButton }
          <div className={ cn(styles.dot, dotClassName) } />
          <Flex gap='none' verticallyCenter>
            { labelTextChild }
            { hintChild }
          </Flex>
        </Flex>
        { displayValueChild }
      </Flex>
      <Input
        name={ name }
        type={ inputType }
        onFocus={ handleInputFocus }
        className={ inputClassName }
        onChange={ !!onChange ? onChange : undefined }
      />
      { textChild || children }
    </label>
  )
}

export default RadioTextCombo
